"use client";

import { c as _c } from "react/compiler-runtime";
import { CompactModeProvider } from "@/contexts/CompactModeContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiWave } from "@/generated/models/ApiWave";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { Transition } from "@headlessui/react";
import { markDropOpenReady } from "@/utils/monitoring/dropOpenTiming";
import { ArrowLeftIcon, ChatBubbleLeftRightIcon } from "@heroicons/react/24/outline";
import React, { Fragment, useCallback, useEffect, useRef, useState } from "react";
import { SingleWaveDropChat } from "./SingleWaveDropChat";
interface SingleWaveDropWrapperProps {
  readonly drop: ApiDrop;
  readonly wave: ApiWave;
  readonly children: React.ReactNode;
  readonly onClose: () => void;
  readonly winningThreshold?: number | null | undefined;
  readonly isVotingClosed?: boolean | undefined;
  readonly isVotingControlsLocked?: boolean | undefined;
}
export const SingleWaveDropWrapper: React.FC<SingleWaveDropWrapperProps> = t0 => {
  const $ = _c(57);
  const {
    drop,
    wave,
    children,
    onClose,
    winningThreshold: t1,
    isVotingClosed: t2,
    isVotingControlsLocked: t3
  } = t0;
  const winningThreshold = t1 === undefined ? null : t1;
  const isVotingClosed = t2 === undefined ? false : t2;
  const isVotingControlsLocked = t3 === undefined ? false : t3;
  const [isChatOpen, setIsChatOpen] = useState(false);
  const isSmallScreen = useMediaQuery("(max-width: 1023px)");
  const readyKeyRef = useRef(null);
  let t4;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setIsChatOpen(_temp);
    };
    $[0] = t4;
  } else {
    t4 = $[0];
  }
  const toggleChat = t4;
  let t5;
  let t6;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      const browserWindow = globalThis.window;
      if (browserWindow === undefined) {
        return;
      }
      const handleClose = () => setIsChatOpen(false);
      browserWindow.addEventListener("single-drop:close-chat", handleClose);
      return () => browserWindow.removeEventListener("single-drop:close-chat", handleClose);
    };
    t6 = [];
    $[1] = t5;
    $[2] = t6;
  } else {
    t5 = $[1];
    t6 = $[2];
  }
  useEffect(t5, t6);
  let t7;
  let t8;
  if ($[3] !== isChatOpen || $[4] !== isSmallScreen) {
    t7 = () => {
      if (!isChatOpen || !isSmallScreen) {
        return;
      }
      const previousOverflow = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = previousOverflow;
      };
    };
    t8 = [isChatOpen, isSmallScreen];
    $[3] = isChatOpen;
    $[4] = isSmallScreen;
    $[5] = t7;
    $[6] = t8;
  } else {
    t7 = $[5];
    t8 = $[6];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[7] !== drop.id || $[8] !== wave.id) {
    t9 = () => {
      const readyKey = `${drop.id}:${wave.id}`;
      if (readyKeyRef.current === readyKey) {
        return;
      }
      readyKeyRef.current = readyKey;
      markDropOpenReady({
        dropId: drop.id,
        waveId: wave.id
      });
    };
    t10 = [drop.id, wave.id];
    $[7] = drop.id;
    $[8] = wave.id;
    $[9] = t10;
    $[10] = t9;
  } else {
    t10 = $[9];
    t9 = $[10];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    t12 = <span className="tw-hidden tw-text-sm tw-font-semibold sm:tw-inline">Close</span>;
    $[11] = t11;
    $[12] = t12;
  } else {
    t11 = $[11];
    t12 = $[12];
  }
  let t13;
  if ($[13] !== onClose) {
    t13 = <div className="tw-flex tw-items-center tw-gap-6"><button type="button" aria-label="Close panel" onClick={onClose} className="tw-flex tw-h-11 tw-w-11 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 tw-transition-all desktop-hover:hover:tw-text-white sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-border-0 sm:tw-bg-transparent sm:tw-px-3 sm:tw-py-2">{t11}{t12}</button></div>;
    $[13] = onClose;
    $[14] = t13;
  } else {
    t13 = $[14];
  }
  const t14 = isChatOpen ? "Hide chat" : "Show chat";
  const t15 = `tw-flex tw-h-11 tw-w-11 tw-flex-shrink-0 tw-items-center tw-justify-center tw-gap-2 tw-rounded-full tw-border tw-border-solid tw-px-3 tw-text-sm tw-font-medium tw-backdrop-blur-md tw-transition-all sm:tw-h-auto sm:tw-w-auto sm:tw-rounded-lg sm:tw-py-2 ${isChatOpen ? "tw-border-iron-700 tw-bg-iron-800 tw-text-iron-100 desktop-hover:hover:tw-bg-iron-700" : "tw-border-iron-700 tw-bg-iron-950 tw-text-iron-300 desktop-hover:hover:tw-bg-iron-800 desktop-hover:hover:tw-text-iron-200"}`;
  let t16;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = <ChatBubbleLeftRightIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0 sm:tw-h-4 sm:tw-w-4" />;
    $[15] = t16;
  } else {
    t16 = $[15];
  }
  const t17 = isChatOpen ? "Hide" : "Show";
  let t18;
  if ($[16] !== t17) {
    t18 = <span className="tw-hidden sm:tw-inline">{t17} Chat</span>;
    $[16] = t17;
    $[17] = t18;
  } else {
    t18 = $[17];
  }
  let t19;
  if ($[18] !== t14 || $[19] !== t15 || $[20] !== t18) {
    t19 = <button onClick={toggleChat} aria-label={t14} className={t15}>{t16}{t18}</button>;
    $[18] = t14;
    $[19] = t15;
    $[20] = t18;
    $[21] = t19;
  } else {
    t19 = $[21];
  }
  let t20;
  if ($[22] !== t13 || $[23] !== t19) {
    t20 = <header className="tw-absolute tw-left-0 tw-right-3 tw-top-0 tw-z-20 tw-bg-iron-950/55 tw-bg-gradient-to-b tw-from-iron-950/75 tw-via-iron-950/55 tw-to-iron-950/10 tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.5rem)] tw-backdrop-blur-sm @[640px]:tw-px-8 lg:tw-px-8"><div className="tw-flex tw-w-full tw-items-center tw-justify-between">{t13}{t19}</div></header>;
    $[22] = t13;
    $[23] = t19;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <div className="tw-h-[calc(env(safe-area-inset-top,0px)+4rem)] tw-shrink-0" aria-hidden="true" />;
    $[25] = t21;
  } else {
    t21 = $[25];
  }
  let t22;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = <div className="tw-h-[env(safe-area-inset-bottom,0px)] tw-shrink-0" aria-hidden="true" />;
    $[26] = t22;
  } else {
    t22 = $[26];
  }
  let t23;
  if ($[27] !== children) {
    t23 = <div className="tw-[scrollbar-gutter:stable] tw-h-full tw-overflow-y-auto tw-overscroll-contain tw-scrollbar-thin tw-scrollbar-track-iron-800 tw-scrollbar-thumb-iron-500 desktop-hover:hover:tw-scrollbar-thumb-iron-300">{t21}{children}{t22}</div>;
    $[27] = children;
    $[28] = t23;
  } else {
    t23 = $[28];
  }
  let t24;
  if ($[29] !== t20 || $[30] !== t23) {
    t24 = <div className="@container tw-relative tw-h-full tw-flex-1 tw-overflow-hidden">{t20}{t23}</div>;
    $[29] = t20;
    $[30] = t23;
    $[31] = t24;
  } else {
    t24 = $[31];
  }
  const t25 = `tw-hidden tw-h-full tw-flex-col tw-bg-black tw-transition-all tw-duration-300 tw-ease-in-out lg:tw-flex ${isChatOpen ? "tw-w-[400px] tw-border-y-0 tw-border-l tw-border-r-0 tw-border-solid tw-border-white/10" : "tw-w-0 tw-overflow-hidden tw-border-none"} `;
  let t26;
  if ($[32] === Symbol.for("react.memo_cache_sentinel")) {
    t26 = <div className="tw-pointer-events-none tw-absolute tw-left-0 tw-right-0 tw-top-0 tw-z-50 tw-h-12 tw-bg-gradient-to-b tw-from-black tw-to-transparent" />;
    $[32] = t26;
  } else {
    t26 = $[32];
  }
  let t27;
  if ($[33] !== drop || $[34] !== isVotingClosed || $[35] !== isVotingControlsLocked || $[36] !== wave || $[37] !== winningThreshold) {
    t27 = <div className="@container tw-relative tw-flex tw-flex-1 tw-flex-col tw-overflow-hidden">{t26}<SingleWaveDropChat key={drop.id} wave={wave} drop={drop} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div>;
    $[33] = drop;
    $[34] = isVotingClosed;
    $[35] = isVotingControlsLocked;
    $[36] = wave;
    $[37] = winningThreshold;
    $[38] = t27;
  } else {
    t27 = $[38];
  }
  let t28;
  if ($[39] !== t25 || $[40] !== t27) {
    t28 = <CompactModeProvider compact={true}><div className={t25}>{t27}</div></CompactModeProvider>;
    $[39] = t25;
    $[40] = t27;
    $[41] = t28;
  } else {
    t28 = $[41];
  }
  let t29;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = <Transition.Child as={Fragment} enter="tw-duration-150 tw-ease-out" enterFrom="tw-opacity-0" enterTo="tw-opacity-100" leave="tw-duration-120 tw-ease-in" leaveFrom="tw-opacity-100" leaveTo="tw-opacity-0"><div className="tw-absolute tw-inset-0 tw-bg-black/60" onClick={toggleChat} /></Transition.Child>;
    $[42] = t29;
  } else {
    t29 = $[42];
  }
  let t30;
  if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <div className="tw-relative tw-z-10 tw-flex tw-flex-none tw-items-center tw-px-2 tw-pb-2 tw-pt-[calc(env(safe-area-inset-top,0px)+0.75rem)] lg:tw-px-8"><button onClick={toggleChat} className="tw-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border-0 tw-bg-transparent tw-px-3 tw-py-2 tw-text-white/70 tw-transition-colors desktop-hover:hover:tw-text-white"><ArrowLeftIcon className="tw-h-5 tw-w-5 tw-flex-shrink-0" /><span className="tw-text-sm tw-font-semibold">Close</span></button></div>;
    $[43] = t30;
  } else {
    t30 = $[43];
  }
  let t31;
  if ($[44] !== drop || $[45] !== isVotingClosed || $[46] !== isVotingControlsLocked || $[47] !== wave || $[48] !== winningThreshold) {
    t31 = <div className="tw-fixed tw-inset-y-0 tw-left-[var(--left-rail,0px)] tw-right-0 tw-z-[90] tw-h-[100dvh] tw-max-h-[100dvh] tw-overflow-hidden tw-overscroll-none lg:tw-hidden">{t29}<Transition.Child as={Fragment} enter="tw-duration-220 tw-transform tw-transition tw-ease-out" enterFrom="tw-translate-x-full" enterTo="tw-translate-x-0" leave="tw-duration-180 tw-transform tw-transition tw-ease-in" leaveFrom="tw-translate-x-0" leaveTo="tw-translate-x-full"><div className="@container tw-absolute tw-inset-0 tw-z-[100] tw-flex tw-h-full tw-min-h-0 tw-transform-gpu tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 tw-will-change-transform lg:tw-hidden">{t30}<div className="@container tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-overflow-hidden"><SingleWaveDropChat key={drop.id} wave={wave} drop={drop} winningThreshold={winningThreshold} isVotingClosed={isVotingClosed} isVotingControlsLocked={isVotingControlsLocked} /></div></div></Transition.Child></div>;
    $[44] = drop;
    $[45] = isVotingClosed;
    $[46] = isVotingControlsLocked;
    $[47] = wave;
    $[48] = winningThreshold;
    $[49] = t31;
  } else {
    t31 = $[49];
  }
  let t32;
  if ($[50] !== isChatOpen || $[51] !== t31) {
    t32 = <CompactModeProvider compact={true}><Transition.Root show={isChatOpen} as={Fragment}>{t31}</Transition.Root></CompactModeProvider>;
    $[50] = isChatOpen;
    $[51] = t31;
    $[52] = t32;
  } else {
    t32 = $[52];
  }
  let t33;
  if ($[53] !== t24 || $[54] !== t28 || $[55] !== t32) {
    t33 = <div className="tw-flex tw-h-full tw-w-full tw-flex-col tw-overflow-hidden tw-overscroll-none tw-bg-iron-950 lg:tw-flex-row">{t24}{t28}{t32}</div>;
    $[53] = t24;
    $[54] = t28;
    $[55] = t32;
    $[56] = t33;
  } else {
    t33 = $[56];
  }
  return t33;
};
function _temp(prev) {
  return !prev;
}